"use client";

import React, { useLayoutEffect, useRef, useState } from "react";

/* ─────────────────────────────────────────────────────────
 * PROMPT BAR
 * Clean, focused composer with brand gradient glow,
 * auto-expanding text input, stop control, and send action.
 * ───────────────────────────────────────────────────────── */

function Icon({ children, size = 15, strokeWidth = 1.8 }: { children: React.ReactNode; size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

export type PromptBarProps = {
  variant?: "Rounded" | "Pill" | string;
  tall?: boolean;
  placeholder?: string;
  onSend?: (text: string) => void;
  onStop?: () => void;
  isLoading?: boolean;
  maxLength?: number;
  gradient?: boolean;
  demo?: boolean;
};

export default function PromptBar({
  variant = "Rounded",
  tall = false,
  placeholder,
  onSend,
  onStop,
  isLoading = false,
  maxLength = 1000,
  gradient = true,
}: PromptBarProps) {
  const pill = variant === "Pill";
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /* Auto-grow textarea up to maxHeight */
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const minHeight = tall ? 64 : 28;
    const maxHeight = 120;
    input.style.height = "0px";
    const contentHeight = input.scrollHeight;
    input.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
    input.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  }, [draft, tall]);

  const canSend = !isLoading && draft.trim().length > 0 && draft.length <= maxLength;

  const send = () => {
    if (!canSend) return;
    onSend?.(draft.trim());
    setDraft("");
  };

  return (
    <div data-promptbar className="w-full">
      <div className="relative">
        <div
          className={`relative isolate flex items-end gap-2 overflow-hidden border border-line bg-surface shadow-card transition-[border-color,border-radius,box-shadow] duration-200 ${
            gradient
              ? "focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100/60"
              : "focus-within:border-line-strong"
          } ${tall ? "p-3.5" : "p-1.5 pl-3"} ${
            pill ? "rounded-full" : tall ? "rounded-[22px]" : "rounded-[14px]"
          }`}
        >
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            disabled={false}
            maxLength={maxLength}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                if (isLoading) return;
                send();
              }
            }}
            placeholder={placeholder ?? "Message MRC AI…"}
            aria-label="Prompt"
            className={`${tall ? "min-h-[64px] py-1 text-[14px] leading-5" : "min-h-7 py-1 text-[13px] leading-[18px]"} min-w-0 flex-1 resize-none bg-transparent text-ink outline-none [overflow-wrap:anywhere] placeholder:text-ink-3`}
          />

          {/* Action button: Stop while loading, Send otherwise */}
          {isLoading ? (
            <button
              type="button"
              aria-label="Stop generating"
              title="Stop generating"
              onClick={onStop}
              className={`flex size-7 shrink-0 items-center justify-center bg-gray-900 text-white transition-[background-color,transform] duration-150 hover:bg-gray-800 active:scale-[0.94] ${
                pill ? "rounded-full" : "rounded-[8px]"
              }`}
            >
              <div className="size-2.5 bg-current rounded-[2px]" />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Send"
              title="Send message"
              disabled={!canSend}
              onClick={send}
              className={`flex size-7 shrink-0 items-center justify-center transition-[background-color,color,transform,opacity] duration-200 enabled:active:scale-[0.94] ${
                pill ? "rounded-full" : "rounded-[8px]"
              } ${canSend && gradient ? "bg-gradient-to-r from-[#3B3E81] to-[#E23338] text-white shadow-sm hover:opacity-90 cursor-pointer" : canSend ? "bg-ink text-surface cursor-pointer" : "bg-line-strong text-ink-3 cursor-not-allowed opacity-60"
              }`}
            >
              <Icon size={16} strokeWidth={2.4}>
                <path d="M12 19V5M5 12l7-7 7 7" />
              </Icon>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
